import { useState } from "react";
import { useLenis } from "lenis/react";
import axios from "axios";
import { toast } from "sonner";
import { Check, ArrowRight, Loader2, CreditCard } from "lucide-react";
import { Reveal, SectionHeading } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOOKUPS = {
  acompte: { Essentielle: "acompte_essentielle", Médium: "acompte_medium", Prestige: "acompte_prestige" },
  solde: { Essentielle: "solde_essentielle", Médium: "solde_medium", Prestige: "solde_prestige" },
};

export const FORMULES = [
  {
    name: "Essentielle",
    price: 800,
    img: "https://images.unsplash.com/photo-1511285560929-80b456fea0bc?w=900&q=80&auto=format&fit=crop",
    description: "L'incontournable pour une soirée de mariage intime et parfaitement rythmée.",
    features: [
      "Sonorisation haute fidélité (cocktail & soirée dansante)",
      "Éclairage dynamique de la piste de danse",
      "Régie DJ professionnelle & micro sans fil",
      "Rendez-vous de préparation & playlist personnalisée",
      "Présence DJ jusqu'à 04 H du matin",
    ],
  },
  {
    name: "Médium",
    price: 1200,
    popular: true,
    img: "https://images.unsplash.com/photo-1519741497674-611481863552?w=900&q=80&auto=format&fit=crop",
    description: "La formule la plus plébiscitée, du vin d'honneur au bout de la nuit.",
    features: [
      "Tout le contenu de la formule Essentielle",
      "Mise en lumière de la salle (projecteurs sur LED)",
      "Sonorisation de la cérémonie laïque ou du vin d'honneur",
      "2 micros sans fil haute portée pour les discours",
      "Vidéoprojecteur avec écran",
      "Présence DJ jusqu'à 04 H du matin",
    ],
  },
  {
    name: "Prestige",
    price: 1600,
    img: "https://images.unsplash.com/photo-1492684223066-81342ee5ff30?w=900&q=80&auto=format&fit=crop",
    description: "Le show intégral sur mesure, pour un mariage spectaculaire et inoubliable.",
    features: [
      "Tout le contenu de la formule Médium",
      "Étincelles froides pour l'ouverture de bal & la pièce montée",
      "Machine à fumée lourde (effet nuage pour la première danse)",
      "Programmation lumière personnalisée (contrôleur DMX)",
      "Présence DJ jusqu'à 04 H du matin",
    ],
  },
];

export default function Packages() {
  const lenis = useLenis();
  const [paying, setPaying] = useState(null);

  const payDeposit = async (formuleName, kind = "acompte") => {
    setPaying(`${kind}-${formuleName}`);
    try {
      const { data } = await axios.post(`${API}/payments/checkout`, {
        lookup_key: LOOKUPS[kind][formuleName],
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch {
      toast.error("Le paiement n'a pas pu être initialisé", {
        description: "Réessayez dans un instant ou contactez-nous.",
      });
      setPaying(null);
    }
  };

  const choose = (idx) => {
    window.dispatchEvent(new CustomEvent("klmprod:formule", { detail: idx }));
    lenis?.scrollTo("#devis", { offset: -72, duration: 1.5 });
  };

  return (
    <section id="formules" className="relative py-28 sm:py-36" data-testid="packages-section">
      <div
        className="absolute left-1/2 top-0 h-[420px] w-[820px] -translate-x-1/2 rounded-full"
        style={{ background: "radial-gradient(closest-side, rgba(0,102,255,0.12), transparent)" }}
      />
      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <SectionHeading
          testId="packages-heading"
          eyebrow="Nos formules mariage"
          title={
            <>
              Des tarifs clairs, <span className="text-gradient">sans surprise.</span>
            </>
          }
          text="Trois formules pensées pour s'adapter à votre journée. Chaque prestation est détaillée dans un devis et un contrat écrit."
        />

        <div className="mt-16 grid grid-cols-1 gap-8 lg:grid-cols-3">
          {FORMULES.map((f, i) => (
            <Reveal key={f.name} delay={i * 0.12} className="h-full">
              <article
                data-testid={`package-card-${f.name.toLowerCase()}`}
                className={`group relative flex h-full flex-col overflow-hidden rounded-3xl border bg-[#0E1524]/80 backdrop-blur-xl transition-transform duration-500 hover:-translate-y-2 ${
                  f.popular
                    ? "border-cyan-400/50 shadow-[0_0_60px_rgba(0,150,255,0.18)]"
                    : "border-cyan-500/15"
                }`}
              >
                {f.popular && (
                  <span
                    className="bg-brand-gradient absolute right-5 top-5 z-10 rounded-full px-3.5 py-1.5 text-[11px] font-bold uppercase tracking-wider text-[#04121f]"
                    data-testid="package-popular-badge"
                  >
                    La plus choisie
                  </span>
                )}
                <div className="relative h-44 overflow-hidden">
                  <img
                    src={f.img}
                    alt={`Ambiance formule ${f.name}`}
                    className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0E1524] via-[#0E1524]/30 to-transparent" />
                </div>

                <div className="flex flex-1 flex-col p-7">
                  <h3 className="font-display text-2xl font-bold text-slate-50">{f.name}</h3>
                  <p className="mt-4 flex items-baseline gap-1.5">
                    <span className="text-sm text-slate-400">à partir de</span>
                    <span className="font-display text-gradient text-4xl font-black" data-testid={`package-price-${f.name.toLowerCase()}`}>
                      {f.price.toLocaleString("fr-FR")} €
                    </span>
                  </p>
                  <p className="mt-3 text-sm leading-relaxed text-slate-400">{f.description}</p>

                  <ul className="mt-6 flex-1 space-y-3">
                    {f.features.map((feat) => (
                      <li key={feat} className="flex items-start gap-2.5 text-sm text-slate-300">
                        <Check size={16} className="mt-0.5 shrink-0 text-cyan-400" />
                        {feat}
                      </li>
                    ))}
                  </ul>

                  <button
                    data-testid={`package-cta-${f.name.toLowerCase()}`}
                    onClick={() => choose(i)}
                    className={`group/btn mt-8 flex w-full items-center justify-center gap-2 rounded-full py-3 text-sm font-bold transition-all duration-300 ${
                      f.popular
                        ? "bg-brand-gradient text-[#04121f] hover:scale-[1.03]"
                        : "border border-cyan-400/30 text-cyan-300 hover:border-cyan-400/60 hover:bg-cyan-400/10"
                    }`}
                  >
                    Choisir cette formule
                    <ArrowRight size={15} className="transition-transform duration-300 group-hover/btn:translate-x-1" />
                  </button>
                  <button
                    data-testid={`package-pay-${f.name.toLowerCase()}`}
                    onClick={() => payDeposit(f.name, "acompte")}
                    disabled={paying === `acompte-${f.name}`}
                    className="mt-3 flex w-full items-center justify-center gap-2 rounded-full border border-emerald-400/30 py-2.5 text-xs font-semibold text-emerald-300 transition-colors duration-300 hover:border-emerald-400/60 hover:bg-emerald-400/10 disabled:opacity-60"
                  >
                    {paying === `acompte-${f.name}` ? <Loader2 size={14} className="animate-spin" /> : <CreditCard size={14} />}
                    Payer l'acompte — {(f.price * 0.3).toLocaleString("fr-FR")} € (30 %)
                  </button>
                  <button
                    data-testid={`package-pay-solde-${f.name.toLowerCase()}`}
                    onClick={() => payDeposit(f.name, "solde")}
                    disabled={paying === `solde-${f.name}`}
                    className="mt-2 flex w-full items-center justify-center gap-2 rounded-full border border-slate-500/30 py-2.5 text-xs font-semibold text-slate-300 transition-colors duration-300 hover:border-slate-400/50 hover:bg-slate-400/10 disabled:opacity-60"
                  >
                    {paying === `solde-${f.name}` ? <Loader2 size={14} className="animate-spin" /> : <CreditCard size={14} />}
                    Régler le solde — {(f.price * 0.7).toLocaleString("fr-FR")} € (70 %)
                  </button>
                </div>
              </article>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.2}>
          <p className="mt-10 text-center text-sm text-slate-500" data-testid="packages-note">
            Séminaires, anniversaires et soirées d'entreprise : parlez-nous de votre projet, nous
            établissons un devis dédié.
          </p>
        </Reveal>
      </div>
    </section>
  );
}
